import LlinkidReferenceModal from '@UI/modals/LlinkidReferenceModal';
import ModalErrorBoundary from '@UI/modals/ModalErrorBoundary';
import type { RootState } from '@generalTypes/rootStateTypes';
import { Modal } from '@kathondvla/react-shared-components/src/components';
import { selectApiWithPendingChangesRelationsToAndFromMap } from '@newStore/documentApi/documentApiSelectors';
import { selectReferenceFrameContent } from '@newStore/referenceFrames/referenceFramesSelectors';
import type { EditAsideReferenceFrameReferences } from '@nodeTypeConfig/configTypes';
import { any } from 'prop-types';
import type React from 'react';
import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as DOCUMENT_ACTIONS from '../../../../reduxLoop/actions/documentActions';
import { useEditHref } from '../../../hooks/UseEditHref';
import AsideLlinkidReferenceItem from '../asideLlinkidReferenceItem/asideLlinkidReferenceItem';
import AsideRelationsPanel from '../asideRelationsPanel/AsideRelationsPanel';
import { useDeleteRelationHook } from '../asideRelationsPanel/useDeleteRelationHook';

const AsideReferenceFrameReferences: React.FC<{
  config: EditAsideReferenceFrameReferences;
  readonly: boolean;
}> = ({ config, readonly }) => {
  const llinkidReferenceModalRef = useRef<Modal>();
  const dispatch = useDispatch();

  const onDelete = useDeleteRelationHook();

  const currentEditingHref = useEditHref();

  const relationOrigin = !config.options.revertedRelationDirection ? 'from' : 'to';
  const relationEnd = !config.options.revertedRelationDirection ? 'to' : 'from';

  const allRelations = useSelector(
    (state: RootState) =>
      selectApiWithPendingChangesRelationsToAndFromMap(state)[relationOrigin][currentEditingHref],
  );

  const referenceFrameContent = useSelector((state: RootState) =>
    selectReferenceFrameContent(state, config.options.referenceFrame),
  );

  const relevantRelations = allRelations.filter(
    (relation) =>
      config.options.relationTypes.some((z) => z === relation.relationtype) &&
      referenceFrameContent[relation[relationEnd].href],
  );

  const onCreate = async () => {
    llinkidReferenceModalRef.current?.open({
      config,
      alreadySelectedReferences: relevantRelations.map((r) => r.from.href),
      onSubmit: (response: { strength: string; $$meta: { permalink: string } }) => {
        if (response) {
          const newRelation = {
            relationtype: config.options.relationTypes[0],
            strength: response.strength,
            from: {
              href: response.$$meta.permalink,
            },
            to: {
              href: currentEditingHref,
            },
          };

          dispatch(DOCUMENT_ACTIONS.addRelationAction(newRelation));
        }
      },
    });
  };

  const onEdit = async (relation) => {
    llinkidReferenceModalRef.current?.open({
      config,
      edit: { relation, item: referenceFrameContent[relation[relationEnd].href] },
      alreadySelectedReferences: relevantRelations.map((r) => r.from.href),
      onSubmit: (response: { strength: string; $$meta: { permalink: string } }) => {
        if (response) {
          dispatch(
            DOCUMENT_ACTIONS.patchRelationAction(relation.key, {
              from: { href: response.$$meta.permalink },
              strength: response.strength,
            }),
          );
        }
      },
    });
  };

  return (
    <>
      <AsideRelationsPanel
        title={config.label}
        items={relevantRelations}
        readOnly={readonly}
        onCreate={onCreate}
        onDelete={onDelete}
        onEdit={onEdit}
      >
        {(item) => (
          <AsideLlinkidReferenceItem
            href={item[relationEnd].href}
            options={config.options}
            rel={item}
          />
        )}
      </AsideRelationsPanel>
      <Modal
        ref={llinkidReferenceModalRef}
        size="sm"
        component={<LlinkidReferenceModal />}
        errorBoundaryComponent={ModalErrorBoundary}
      />
    </>
  );
};

AsideReferenceFrameReferences.displayName = 'AsideReferenceFrameReferences';

AsideReferenceFrameReferences.propTypes = {
  config: any,
  readonly: any,
};

export default AsideReferenceFrameReferences;
